import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ShieldCheck, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BLANK = { framework: "", stages: [], standard_risks: [] };
const BLANK_RISK = { title: "", description: "", risk_type: "Delivery", probability_score: 3, impact_score: 3, owner: "" };
const SCORES = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

export default function AdminFrameworks() {
  const [configs, setConfigs] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () =>
    api.get("/framework-configs").then(({ data }) => setConfigs(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load frameworks"));

  useEffect(() => {
    load();
    api.get("/config/lists").then(({ data }) => {
      const byKey = Object.fromEntries(data.map((l) => [l.key, l.values]));
      setTypes(byKey.risk_type || []);
    }).catch(() => {});
  }, []);

  const openConfig = (c) => {
    setEditing(c || null);
    setForm(c ? { ...BLANK, ...c, stages: c.stages || [], standard_risks: c.standard_risks || [] } : BLANK);
    setOpen(true);
  };

  const save = async () => {
    if (!form.framework.trim()) return toast.error("Framework name is required");
    const payload = {
      framework: form.framework.trim(),
      stages: form.stages.map((s) => s.trim()).filter(Boolean),
      standard_risks: form.standard_risks
        .filter((r) => r.title?.trim())
        .map((r) => ({
          ...r,
          probability_score: Number(r.probability_score) || 3,
          impact_score: Number(r.impact_score) || 3,
        })),
    };
    try {
      if (editing) await api.put(`/framework-configs/${editing.id}`, payload);
      else await api.post("/framework-configs", payload);
      setOpen(false);
      await load();
      toast.success(editing ? "Framework updated" : "Framework created");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the framework template "${c.framework}"?`)) return;
    try {
      await api.delete(`/framework-configs/${c.id}`);
      await load();
      toast.success("Framework deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setRisk = (i, key, value) =>
    setForm((f) => ({ ...f, standard_risks: f.standard_risks.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)) }));

  return (
    <Shell
      testId="admin-frameworks-page"
      title="Governance Frameworks"
      subtitle="Stage sequences and standard risks applied automatically when a project uses the framework."
      actions={
        <Button data-testid="new-framework-btn" onClick={() => openConfig(null)} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
          <Plus size={15} /> New Framework
        </Button>
      }
    >
      {configs.length === 0 ? (
        <Empty testId="frameworks-empty" text="No framework templates yet." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {configs.map((c) => (
            <div key={c.id} data-testid={`framework-card-${c.id}`} className="panel p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-900">{c.framework}</h3>
                  <div className="mt-0.5 text-xs text-slate-400">
                    {(c.stages || []).length} stages · {(c.standard_risks || []).length} standard risks
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`edit-framework-${c.id}`} onClick={() => openConfig(c)} aria-label="Edit framework" title="Edit framework" className="text-slate-400 hover:text-slate-800">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={`delete-framework-${c.id}`} onClick={() => remove(c)} aria-label="Delete framework" title="Delete framework" className="text-slate-300 hover:text-red-600">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-600">
                {(c.stages || []).join(" → ") || "No stages defined"}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-slate-500">
                {(c.standard_risks || []).slice(0, 5).map((r, i) => <li key={i} className="truncate">• {r.title}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="framework-dialog" className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Framework" : "New Framework"}</DialogTitle>
            <DialogDescription>The first stage becomes the project's opening gate. Standard risks are pre-loaded onto the risk register.</DialogDescription>
          </DialogHeader>

          <div><label className="field-label">Framework Name *</label><Input data-testid="framework-name-input" value={form.framework} onChange={(e) => setForm({ ...form, framework: e.target.value })} /></div>

          <div className="mt-2">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-display text-sm font-semibold text-slate-800">Stages / Gates</h4>
              <Button size="sm" variant="outline" data-testid="add-framework-stage-btn" onClick={() => setForm((f) => ({ ...f, stages: [...f.stages, ""] }))} className="gap-1.5">
                <Plus size={13} /> Add Stage
              </Button>
            </div>
            {form.stages.length === 0 ? (
              <p className="rounded border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">No stages yet.</p>
            ) : (
              <div className="space-y-2">
                {form.stages.map((s, i) => (
                  <div key={i} data-testid={`framework-stage-row-${i}`} className="flex items-center gap-2">
                    <Input data-testid={`framework-stage-${i}`} value={s} placeholder={`Stage ${i + 1}`} onChange={(e) => setForm((f) => ({ ...f, stages: f.stages.map((x, idx) => (idx === i ? e.target.value : x)) }))} />
                    <button data-testid={`remove-framework-stage-${i}`} onClick={() => setForm((f) => ({ ...f, stages: f.stages.filter((_, idx) => idx !== i) }))} aria-label="Remove stage" title="Remove stage" className="text-slate-300 hover:text-red-600">
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-3">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="font-display text-sm font-semibold text-slate-800">Standard Risks</h4>
              <Button size="sm" variant="outline" data-testid="add-framework-risk-btn" onClick={() => setForm((f) => ({ ...f, standard_risks: [...f.standard_risks, { ...BLANK_RISK }] }))} className="gap-1.5">
                <Plus size={13} /> Add Risk
              </Button>
            </div>
            {form.standard_risks.length === 0 ? (
              <p className="rounded border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">No standard risks yet.</p>
            ) : (
              <div className="space-y-2">
                {form.standard_risks.map((r, i) => (
                  <div key={i} data-testid={`framework-risk-row-${i}`} className="grid grid-cols-12 items-end gap-2 rounded border border-slate-200 p-2">
                    <div className="col-span-5"><label className="field-label">Title</label><Input data-testid={`framework-risk-title-${i}`} value={r.title} onChange={(e) => setRisk(i, "title", e.target.value)} /></div>
                    <div className="col-span-3"><label className="field-label">Type</label><Select testId={`framework-risk-type-${i}`} value={r.risk_type} onChange={(v) => setRisk(i, "risk_type", v)} options={types} placeholder="Type" /></div>
                    <div className="col-span-2"><label className="field-label">Prob</label><Select testId={`framework-risk-prob-${i}`} value={r.probability_score} onChange={(v) => setRisk(i, "probability_score", v)} options={SCORES} placeholder="3" /></div>
                    <div className="col-span-1"><label className="field-label">Imp</label><Select testId={`framework-risk-impact-${i}`} value={r.impact_score} onChange={(v) => setRisk(i, "impact_score", v)} options={SCORES} placeholder="3" /></div>
                    <div className="col-span-1 pb-2">
                      <button data-testid={`remove-framework-risk-${i}`} onClick={() => setForm((f) => ({ ...f, standard_risks: f.standard_risks.filter((_, idx) => idx !== i) }))} aria-label="Remove risk" title="Remove risk" className="text-slate-300 hover:text-red-600">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" data-testid="framework-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="framework-save-btn" onClick={save} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <ShieldCheck size={14} /> {editing ? "Save Framework" : "Create Framework"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
